/**
 * depoimentos.ts — Categoria "Depoimentos" (10 modelos premium)
 *
 * Prova social montada com seção em camadas, cartões, avatares e fotos
 * do acervo compartilhado.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote A)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { AVATARES, FOTO, WEB, b } from './_shared';

/** Atalho para um depoimento em cartão. */
function depo(texto: string, autor: string, variante = 'solid') {
  return b('ui.card', { icon: '“', title: texto, text: autor, variant: variante, radius: 'xl', shadow: 'md', motion: 'fade-up' });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'depoimentos.tres-cartoes',
    category: 'depoimentos',
    label: 'Três depoimentos',
    description: 'Título e três cartões de clientes lado a lado.',
    preview: 'grid-3',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade' }, [
        b('ui.title', { text: 'O que dizem nossos clientes', size: 'lg', level: 'h2', align: 'center' }),
        depo('Reduzimos pela metade o tempo de fechamento de cada proposta.', 'Ana Ribeiro — Diretora Comercial'),
        depo('O suporte responde rápido e resolve. Isso mudou o nosso dia a dia.', 'Marcos Lima — Gerente de Operações'),
        depo('Em três meses todo o time já estava usando sem dificuldade.', 'Paula Souza — Sócia'),
      ]),
    ],
  },

  {
    key: 'depoimentos.citacao-grande',
    category: 'depoimentos',
    label: 'Uma citação grande',
    description: 'Depoimento único em destaque, com foto do cliente.',
    preview: 'split-left',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1000px', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade-up' }, [
        b('ui.image', { src: FOTO.retrato, alt: 'Cliente entrevistado', ratio: 'square', radius: 'full', shadow: 'lg' }),
        b('ui.title', { text: '“Passamos a vender mais sem contratar ninguém.”', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Ana Ribeiro — Diretora Comercial na Construtora Vega', size: 'sm' }),
      ]),
    ],
  },

  {
    key: 'depoimentos.escuro-vidro',
    category: 'depoimentos',
    label: 'Depoimentos em fundo escuro',
    description: 'Fundo escuro com cartões de vidro e brilho decorativo.',
    preview: 'grid-2',
    outputs: WEB,
    order: 3,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'xl', align: 'center', maxWidth: '1080px', layout: 'bento', backgroundGradient: 'mesh-dark', glow: 'bottom-left', textColor: '#ffffff', motion: 'fade' },
        [
          b('ui.badge', { text: 'Histórias reais', variant: 'glass', align: 'center' }),
          depo('Tudo o que era planilha virou processo. Ganhamos previsibilidade.', 'Rafael Prado — CEO', 'glass'),
          depo('O time comercial parou de perder oportunidade por esquecimento.', 'Carla Mendes — Head de Vendas', 'glass'),
        ],
      ),
    ],
  },

  {
    key: 'depoimentos.com-nota',
    category: 'depoimentos',
    label: 'Depoimentos com nota média',
    description: 'Prova social com fotos, nota média e dois depoimentos.',
    preview: 'grid-2',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', layout: 'bento', motion: 'fade-up' }, [
        b('ui.avatars', { items: AVATARES, text: '+2.000 clientes atendidos', note: 'Nota 4,9 de 5 em avaliações', align: 'center' }),
        depo('Atendimento excelente e entrega no prazo combinado.', 'Bruno Alves — Financeiro'),
        depo('A migração foi tranquila e não paramos a operação.', 'Letícia Faria — Coordenadora'),
      ]),
    ],
  },

  {
    key: 'depoimentos.quatro',
    category: 'depoimentos',
    label: 'Quatro depoimentos',
    description: 'Grade com quatro cartões contornados.',
    preview: 'grid-4',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', backgroundColor: '#f8fafc', motion: 'fade' }, [
        b('ui.title', { text: 'Quem já usa recomenda', size: 'lg', level: 'h2', align: 'center' }),
        depo('Simples de usar e fácil de treinar o time.', 'João Pereira — Supervisor', 'outline'),
        depo('Ganhamos controle total das cobranças.', 'Fernanda Dias — Financeiro', 'outline'),
        depo('Relatórios prontos economizam horas por semana.', 'Ricardo Nunes — Diretor', 'outline'),
        depo('O suporte é humano e resolve de primeira.', 'Camila Rocha — Atendimento', 'outline'),
      ]),
    ],
  },

  {
    key: 'depoimentos.foto-fundo',
    category: 'depoimentos',
    label: 'Depoimento sobre foto',
    description: 'Foto de ambiente ao fundo com a citação por cima.',
    preview: 'hero-image',
    outputs: WEB,
    order: 6,
    build: () => [
      b(
        'section.layered',
        { backgroundImage: FOTO.escritorio, overlay: 'darker', paddingY: 'xl', align: 'center', textColor: '#ffffff', maxWidth: '900px', motion: 'fade' },
        [
          b('ui.title', { text: '“Em seis meses dobramos o número de contratos ativos.”', size: 'lg', level: 'h2', align: 'center' }),
          b('ui.paragraph', { text: 'Marcos Lima — Gerente de Operações na TransSul', align: 'center', size: 'sm' }),
        ],
      ),
    ],
  },

  {
    key: 'depoimentos.caso-de-sucesso',
    category: 'depoimentos',
    label: 'Caso de sucesso com números',
    description: 'Depoimento acompanhado de dois resultados medidos.',
    preview: 'split-right',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', pattern: 'grid', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Caso de sucesso', variant: 'soft' }),
        b('ui.title', { text: '“Trocamos três sistemas por um só.”', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Paula Souza — Sócia na Clínica Vitta', size: 'sm' }),
        b('ui.stat', { value: '-40%', label: 'de custo com ferramentas' }),
        b('ui.image', { src: FOTO.consultoria, alt: 'Cliente em reunião', ratio: 'landscape', radius: 'xl', shadow: 'lg' }),
      ]),
    ],
  },

  {
    key: 'depoimentos.dois-lado-a-lado',
    category: 'depoimentos',
    label: 'Dois depoimentos amplos',
    description: 'Dois cartões grandes, com respiro e sombra suave.',
    preview: 'grid-2',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', layout: 'bento', backgroundGradient: 'soft', motion: 'fade' }, [
        depo('A empresa inteira passou a falar a mesma língua sobre os números.', 'Rafael Prado — CEO'),
        depo('O acompanhamento mensal faz diferença: sempre sai um plano de ação.', 'Carla Mendes — Head de Vendas'),
      ]),
    ],
  },

  {
    key: 'depoimentos.com-logos',
    category: 'depoimentos',
    label: 'Depoimentos com marcas',
    description: 'Faixa de marcas atendidas acima dos depoimentos.',
    preview: 'logos',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade-up' }, [
        b('ui.logos', { note: 'Empresas que confiam na gente', grayscale: true, align: 'center', items: '' }),
        depo('Confiamos porque entregam o que prometem.', 'Bruno Alves — Financeiro'),
        depo('Excelente relação entre preço e resultado.', 'Letícia Faria — Coordenadora'),
      ]),
    ],
  },

  {
    key: 'depoimentos.convite',
    category: 'depoimentos',
    label: 'Depoimento com convite',
    description: 'Citação forte seguida de botão para falar com o time.',
    preview: 'cta',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '900px', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade' }, [
        b('ui.title', { text: '“Valeu cada centavo. Faria de novo.”', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.paragraph', { text: 'Camila Rocha — Atendimento na Rede Bem Estar', align: 'center', size: 'sm' }),
        b('ui.buttons', { items: 'Quero falar com o time|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },
];

registerPatterns(modelos);
